// Cliente HTTP del canal interno (asistente de gerencia) del helpdesk.
//
// Calca el patrón de aiSettingsApi.js: mismo BFF, mismos headers Bearer, mismos
// errores tipados con `.status` para distinguir "backend aún no desplegado"
// (isEndpointMissing) de un error real. Se agrega `.code` porque el contrato
// usa 409 NO_INTERNAL_AGENT como señal de negocio (no hay agente con audiencia
// `internal` activo), no como fallo técnico.
//
// El hilo es SIEMPRE el del usuario logueado: el backend lo resuelve por el
// token, así que acá no viaja ningún id de conversación ni de usuario.

// isEndpointMissing se re-exporta desde el módulo que ya lo implementa: una
// sola definición de "ruta no desplegada" para todo el helpdesk.
import { isEndpointMissing } from "../ai/aiSettingsApi";

// Typed error shared with the other helpdesk clients (status, code and the
// backend validation lists in err.messages).
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

export { isEndpointMissing };

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

const INTERNAL_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/internal`;

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * true cuando el tenant no tiene un agente con audiencia `internal` activo. El
 * backend responde 409 con `code`; el status solo también vale para no depender
 * de que el BFF propague el campo.
 * @param {unknown} err
 * @returns {boolean}
 */
export const isNoInternalAgent = (err) =>
  err?.code === "NO_INTERNAL_AGENT" || (err?.status === 409 && !err?.code);

/**
 * Hilo interno del usuario logueado. Se crea en el backend al primer mensaje,
 * así que antes de eso responde con `messages: []` — nunca 404 por "no hay
 * conversación todavía".
 * Contrato: { ok, conversation_id, agent: { id, name, avatar } | null,
 *   messages: [{ id, sender_role: 'user'|'ai'|'system', content, createdAt,
 *   metadata? }] }
 * @returns {Promise<object>}
 */
export async function getInternalConversation() {
  const response = await authFetch(`${INTERNAL_PATH}/conversation`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Envía un mensaje al asistente interno. El backend espera la respuesta de la
 * IA hasta 25 s: `reply` puede volver en null y el caller sondea el GET hasta
 * que aparezca.
 * @param {string} text - 1..2000 caracteres (el backend también lo valida)
 * @returns {Promise<{ok: boolean, conversation_id: string, message: object,
 *   reply: object|null}>}
 */
export async function postInternalChat(text) {
  const response = await authFetch(`${INTERNAL_PATH}/chat`, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}
